import { useEffect, useState } from "react";
import { TgScreen } from "../components/TgScreen";
import { Card } from "../components/atoms";
import { SHIELDED_BASE_URL } from "../shielded/config";
import { haptic } from "../lib/haptics";

interface AttestationSummary {
  mode: string;
  policy: string;
  silicon: string;
  measurement: string;
  report_data: string;
}

/**
 * The "why should I trust this" page.
 *
 * Three things the user genuinely needs to know:
 *   1. The mnemonic on the device is the source of truth — full self-custody.
 *   2. The scanner backend never sees their spending key, only a UFVK,
 *      and proves that fact with an SEV-SNP attestation we link to.
 *   3. What can go wrong + how it's recoverable.
 *
 * Everything else (acronyms, RFC numbers, ZIP references) earns its
 * place only where it helps the reader verify a claim.
 */
export function SecurityScreen() {
  const [att, setAtt] = useState<AttestationSummary | null>(null);
  const [attErr, setAttErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch(`${SHIELDED_BASE_URL}/attestation`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as AttestationSummary;
        if (!cancelled) setAtt(j);
      } catch (e) {
        if (!cancelled) {
          setAttErr(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <TgScreen title="Security" bottomNav="security">
      <div style={{ padding: "8px 16px 24px" }}>
        {/* ── 1. Self-custody ─────────────────────────────────────────── */}
        <Section icon="🔑" headline="Your keys, your wallet">
          The 12-word recovery phrase is generated on your device and never
          leaves it. We don&apos;t have it. Telegram doesn&apos;t have it.
          Anyone who has those 12 words controls the funds — that is the
          point.
          <Detail>
            Phrase → BIP-39 seed → ZIP-32 derivation → Sapling spending key.
            Encrypted at rest with a 256-bit AES key generated on this device.
          </Detail>
        </Section>

        {/* ── 2. The dual store ───────────────────────────────────────── */}
        <Section icon="🗄️" headline="How storage works">
          The encrypted phrase lives in Telegram&apos;s CloudStorage (syncs
          across all your TG devices). The decryption key lives only on
          this device. Telegram has the ciphertext; only you have the key.
          <Detail>
            Reinstall TG, switch phones, lose your laptop — the ciphertext
            survives. If a device-local key is gone, paste the recovery
            phrase once and we re-encrypt with a fresh key.
          </Detail>
        </Section>

        {/* ── 3. UFVK + the scanner enclave ───────────────────────────── */}
        <Section icon="🛡️" headline="Why we send the UFVK">
          To show your balance we need to scan every Zcash block for
          incoming notes addressed to you. Doing that on the phone would
          take hours and burn the battery. Instead we run the scanner on
          a server — but the server runs inside an{" "}
          <b>AMD SEV-SNP enclave</b> we can&apos;t see into. We send only
          the <b>UFVK</b> (Unified Full Viewing Key): read-only, can
          observe incoming notes but{" "}
          <i>cannot sign, spend, or move funds</i>.
          <Detail>
            The UFVK is HPKE-sealed (RFC 9180) to the enclave&apos;s
            ephemeral public key. The enclave attests with an SEV-SNP
            report; we verify the silicon signature on every session.
          </Detail>
        </Section>

        {/* ── Live attestation ────────────────────────────────────────── */}
        <Card
          onClick={() => haptic("light")}
          style={{ padding: 14, marginBottom: 14, cursor: "pointer" }}
        >
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "var(--hint)",
              marginBottom: 8,
            }}
          >
            Live enclave attestation
          </div>
          {att ? (
            <>
              <KV label="Silicon" value={att.silicon} />
              <KV label="Mode" value={att.mode.toUpperCase()} />
              <KV label="Policy" value={att.policy} />
              <KV
                label="Measurement"
                value={`${att.measurement.slice(0, 16)}…${att.measurement.slice(-16)}`}
                mono
              />
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: "var(--hint)",
                  lineHeight: 1.5,
                }}
              >
                The measurement is a hash of the exact binary running inside
                the enclave. Any tampering changes it.
              </div>
            </>
          ) : attErr ? (
            <div style={{ fontSize: 12, color: "var(--destructive, #d33)" }}>
              Couldn&apos;t fetch attestation: {attErr}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "var(--hint)" }}>
              Fetching attestation…
            </div>
          )}
        </Card>

        {/* ── 4. Signing happens on-device ────────────────────────────── */}
        <Section icon="✍️" headline="Spending happens here, not there">
          Every transaction is built and signed inside your browser using a
          local WebAssembly module. The proving keys (~26 MB) are
          point-compressed bls12-381 shards we ship once, then decompress
          and cache on your device. Nothing about a spend leaves the
          device until the signed transaction is broadcast.
          <Detail>
            sapling-crypto WASM (Rust → wasm32) for Groth16 proving. We
            never POST your spending authority anywhere.
          </Detail>
        </Section>

        {/* ── 5. What can go wrong ────────────────────────────────────── */}
        <Section icon="⚠️" headline="The one thing you must do">
          Back up the 12-word recovery phrase. If you lose your phone and
          never wrote them down, the funds are gone — there is no
          customer-support recovery for a non-custodial wallet. The
          balance banner on the Wallet tab nags you when this matters.
        </Section>

        {/* ── Footer links ────────────────────────────────────────────── */}
        <div
          style={{
            fontSize: 12,
            color: "var(--hint)",
            textAlign: "center",
            marginTop: 18,
            lineHeight: 1.6,
          }}
        >
          <a
            href={`${SHIELDED_BASE_URL.startsWith("http") ? SHIELDED_BASE_URL : "https://shielded.leokit.dev"}/attestation`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => haptic("light")}
            style={{ color: "var(--link)" }}
          >
            View raw attestation →
          </a>
          <div style={{ marginTop: 4 }}>Audited primitives</div>
        </div>
      </div>
    </TgScreen>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

function Section({
  icon,
  headline,
  children,
}: {
  icon: string;
  headline: string;
  children: React.ReactNode;
}) {
  return (
    <Card style={{ padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div style={{ fontSize: 22, lineHeight: 1, marginTop: 1 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
            {headline}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--text-soft)",
              lineHeight: 1.55,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Detail({ children }: { children: React.ReactNode }) {
  return (
    <details style={{ marginTop: 8 }}>
      <summary
        onClick={() => haptic("light")}
        style={{
          fontSize: 11,
          color: "var(--hint)",
          textTransform: "uppercase",
          letterSpacing: 1,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        Technical detail
      </summary>
      <div
        style={{
          marginTop: 6,
          fontSize: 12,
          color: "var(--hint)",
          lineHeight: 1.5,
        }}
      >
        {children}
      </div>
    </details>
  );
}

function KV({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 8,
        paddingTop: 6,
      }}
    >
      <span style={{ fontSize: 12, color: "var(--hint)" }}>{label}</span>
      <span
        style={{
          fontSize: 12,
          fontFamily: mono ? "var(--font-mono, monospace)" : undefined,
          color: "var(--text)",
          wordBreak: "break-all",
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}
